import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_TAB, StatusTabValue } from '~components/Filters/OrdersStatusFilter';
import { NavigationTabs } from '~components/LayoutComponents/NavigationTabs';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import { DispatchURLParams } from '~constants/dispatch';
import {
  OrdersAssignedStorageKeys,
  OrdersAssignedURLParams,
} from '~constants/ordersAssigned';
import { useStores } from '~store';

import AssignedOrdersView, { AssignedOrdersViewTab } from './AssignedOrdersView';

const getInitialView = (
  searchParams: URLSearchParams,
  defaultView: AssignedOrdersViewTab,
) => {
  const viewParam = searchParams.get(
    OrdersAssignedURLParams.VIEW,
  ) as AssignedOrdersViewTab;
  const storedView = localStorage.getItem(
    OrdersAssignedStorageKeys.VIEW_PARAM,
  ) as AssignedOrdersViewTab;
  const view = viewParam || storedView;

  return Object.values(AssignedOrdersViewTab).includes(view) ? view : defaultView;
};

const getInitialStatusTab = (searchParams: URLSearchParams) => {
  const tabParam = searchParams.get(DispatchURLParams.TAB) as StatusTabValue;
  const storedTab = localStorage.getItem(
    OrdersAssignedStorageKeys.TAB_PARAM,
  ) as StatusTabValue;
  const tab = tabParam || storedTab;

  return Object.values(StatusTabValue).includes(tab) ? tab : DEFAULT_TAB;
};

function AssignedOrdersProvider() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userStore } = useStores();
  const userAccess = userStore.getAccess();

  const defaultViewForAccess = useMemo(() => {
    if (userAccess.assignedTabDriver) {
      return AssignedOrdersViewTab.DRIVE;
    }

    if (userAccess.assignedTabForeman) {
      return AssignedOrdersViewTab.FOREMAN;
    }

    if (userAccess.assignedTabSalesperson) {
      return AssignedOrdersViewTab.SALES;
    }

    return AssignedOrdersViewTab.COLLABORATOR;
  }, [userAccess]);

  const view = searchParams.get(OrdersAssignedURLParams.VIEW) as AssignedOrdersViewTab;
  const isValidView = Object.values(AssignedOrdersViewTab).includes(view);

  const isLoading = !isValidView;

  useEffect(() => {
    if (isValidView) {
      localStorage.setItem(OrdersAssignedStorageKeys.VIEW_PARAM, view);
    }
  }, [isValidView, view]);

  useEffect(() => {
    setSearchParams((params) => {
      const view = getInitialView(params, defaultViewForAccess);
      params.set(OrdersAssignedURLParams.VIEW, view);

      return params;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (view === AssignedOrdersViewTab.FOREMAN) {
      setSearchParams((params) => {
        params.set(OrdersAssignedURLParams.TAB, getInitialStatusTab(params));

        return params;
      });
    }
  }, [view]);

  if (isLoading) {
    return <CommonLoader />;
  }

  return <AssignedOrders view={view} />;
}

interface AssignedOrdersProps {
  view: AssignedOrdersViewTab;
}

function AssignedOrders({ view }: AssignedOrdersProps) {
  const theme = useTheme();
  const { userStore } = useStores();
  const userAccess = userStore.getAccess();
  const navItems = [];

  if (userAccess.assignedTabDriver) {
    navItems.push({
      title: t('page_headings.drive'),
      link: '/assigned?view=drive',
    });
  }

  if (userAccess.assignedTabForeman) {
    navItems.push(
      {
        title: t('page_headings.foreman'),
        link: '/assigned?view=foreman',
      },
      {
        title: t('page_headings.collaborator'),
        link: '/assigned?view=collaborator',
      },
    );
  }

  if (userAccess.assignedTabSalesperson) {
    navItems.push({
      title: t('page_headings.sales'),
      link: '/assigned?view=sales',
    });
  }

  const isForemanView = view === AssignedOrdersViewTab.FOREMAN;

  return (
    <Box
      sx={{
        bgcolor: theme.brandV2.colors.treadGray8,
        display: 'grid',
        gridAutoRows: isForemanView ? 'auto auto auto 1fr' : 'auto auto 1fr',
        gridTemplateColumns: '1fr',
        height: '100%',
        mx: -5,
        width: `calc(100% + ${theme.spacing(5 * 2)})`,
      }}
    >
      <AssignedOrdersView
        viewSelector={<NavigationTabs navItems={navItems} />}
        view={view}
      />
    </Box>
  );
}

export default AssignedOrdersProvider;
